// UserContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import apiClient from "../utils/apiClient";

const UserContext = createContext();
// Separate from UserContext so refetchUser/isLoading stay callable pre-login,
// when useUser() is legitimately null.
const UserActionsContext = createContext();

export const UserProvider = ({ children }) => {
  const [userDetails, setUserDetails] = useState(null);
  // True until the first /users/details call resolves — lets consumers (see
  // AuthProvider's authStatus) tell "session not established yet" apart from
  // "confirmed logged out", which matters now that the answer is async
  // instead of a synchronous localStorage read.
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserDetails = useCallback(async () => {
    try {
      const response = await apiClient.get("/users/details");
      setUserDetails(response.data);
    } catch (error) {
      setUserDetails(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  // Also spread onto the data object for existing useUser().refetchUser call sites.
  const contextValue = userDetails ? { ...userDetails, refetchUser: fetchUserDetails } : null;

  return (
    <UserActionsContext.Provider value={{ refetchUser: fetchUserDetails, isLoading }}>
      <UserContext.Provider value={contextValue}>
        {children}
      </UserContext.Provider>
    </UserActionsContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
// Use this (instead of useUser().refetchUser) when the caller may run
// before userDetails has ever loaded — e.g. Login, pre-authentication.
export const useUserActions = () => useContext(UserActionsContext);
